package er.prateek.crossly.progression

import er.prateek.crossly.data.model.DifficultyTier
import er.prateek.crossly.data.model.GameMode
import er.prateek.crossly.di.AppContainer
import er.prateek.crossly.firebase.AnalyticsEvents
import er.prateek.crossly.ui.navigation.Destination

/**
 * "Brainstorm" -- a mixed run that rotates Alphabets (word crossword) -> Mathematics (math
 * crossword) -> Riddles, one level of each per round, round after round. Between games the
 * player lands on a short break screen ([Destination.BrainstormBreak]) rather than the normal
 * results screen. Purely in-memory: Home calls [stop] on arrival, so backing out of any level
 * ends the run, and nothing persists across launches.
 *
 * The session only chooses *what to play next*; stars, streak and badges are recorded by the
 * play screens exactly as in normal play.
 */
object BrainstormSession {
    val order = listOf(GameMode.CROSSWORD, GameMode.MATH_CROSSWORD, GameMode.RIDDLES)

    /** Games finished in this run. */
    var step = 0
        private set

    var active: Boolean = false
        private set

    /** 1-based round the *next* game belongs to. */
    val round: Int get() = step / order.size + 1

    /** Position (0..2) of the next game within its round. */
    val position: Int get() = step % order.size

    val upcomingMode: GameMode get() = order[position]

    fun start() {
        active = true
        step = 0
    }

    /**
     * Ends the run, logging [AnalyticsEvents.BRAINSTORM_ENDED] once however it ended: the break
     * screen's End button, or backing out of a game to Home. A no-op when no run is active.
     */
    fun stop(reason: String) {
        if (!active) return
        active = false
        AppContainer.analytics.logEvent(
            AnalyticsEvents.BRAINSTORM_ENDED,
            mapOf("reason" to reason, "games_played" to step, "rounds_completed" to step / order.size),
        )
    }

    /** Marks the current game finished. Returns true when that game completed a full round. */
    fun onGameFinished(): Boolean {
        step++
        return position == 0
    }

    /** The next unplayed level of [upcomingMode]. */
    suspend fun nextDestination(): Destination = when (upcomingMode) {
        GameMode.CROSSWORD -> {
            val puzzles = DifficultyTier.entries.flatMap { AppContainer.contentRepository.puzzlesByTier(it) }
            val target = puzzles.firstOrNull { AppContainer.progressionManager.starsFor(it.id) == 0 } ?: puzzles.first()
            Destination.Crossword(target.id)
        }
        GameMode.MATH_CROSSWORD ->
            Destination.MathCrossword(
                nextLevel(AppContainer.mathProgressionManager, AppContainer.appConfig().modes.mathCrossword.levelsPerTier),
            )
        GameMode.RIDDLES ->
            Destination.RiddleRun(
                nextLevel(AppContainer.riddleProgressionManager, AppContainer.appConfig().modes.riddles.levelsPerTier),
            )
    }

    /** First unstarred level the player can open, falling back to the very first level. */
    private fun nextLevel(progression: ModeProgressionManager, levelsPerTier: Int): String {
        for (tier in DifficultyTier.entries) {
            for (i in 1..levelsPerTier) {
                val id = progression.levelId(tier, i)
                if (progression.starsFor(id) == 0 && progression.isLevelUnlocked(tier, i - 1)) return id
            }
        }
        return progression.levelId(DifficultyTier.EASY, 1)
    }
}
